import { create } from 'zustand';
import type { PublicStore } from '@/features/menu/types';

const STORAGE_KEY = 'fnbCustomer.browseStore.v1';
const SCHEMA_VERSION = 4;

export type BrowseStoreSelectionSource = 'qr' | 'manual' | 'nearest' | 'fallback';

export interface BrowseStore {
  storeCode: string;
  name: string;
  address?: string;
  addressLine?: string;
  provinceName?: string;
  communeName?: string;
  communeType?: string;
  postalCode?: string;
  latitude?: number;
  longitude?: number;
  acceptingOrders?: boolean;
  selectionSource: BrowseStoreSelectionSource;
}

interface PersistedBrowseStore {
  version?: unknown;
  store?: unknown;
}

interface BrowseStoreState {
  store: BrowseStore | null;
  selectStore: (store: Omit<BrowseStore, 'selectionSource'>, source?: BrowseStoreSelectionSource) => void;
  clearStore: () => void;
}

export function browseStoreFromPublic(store: PublicStore): Omit<BrowseStore, 'selectionSource'> {
  return {
    storeCode: store.storeCode ?? '',
    name: store.name ?? store.storeCode ?? '',
    address: store.address,
    addressLine: store.addressLine,
    provinceName: store.provinceName,
    communeName: store.communeName,
    communeType: store.communeType,
    postalCode: store.postalCode,
    latitude: store.latitude,
    longitude: store.longitude,
    acceptingOrders: store.acceptingOrders,
  };
}

function restore(): BrowseStore | null {
  if (typeof window === 'undefined') return null;
  try {
    const value = JSON.parse(
      window.localStorage.getItem(STORAGE_KEY) ?? 'null'
    ) as PersistedBrowseStore | null;
    const stored = readStoredBrowseStore(value?.store);
    if (!stored) return null;
    // Earlier versions are display-only preferences. Migrate only allow-listed Store fields;
    // this storage path never reads or modifies Cart recovery state.
    if (value?.version === 1 || value?.version === 2 || value?.version === 3) {
      const migrated = normalize(stored, 'manual');
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify({ version: SCHEMA_VERSION, store: migrated }));
      return migrated;
    }
    const persistedStore = asRecord(value?.store);
    if (value?.version !== SCHEMA_VERSION || persistedStore?.selectionSource !== 'manual') {
      window.localStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return normalize(stored, 'manual');
  } catch {
    window.localStorage.removeItem(STORAGE_KEY);
    return null;
  }
}

const initial = restore();

function validCoordinate(value: unknown, min: number, max: number): number | undefined {
  return typeof value === 'number' && Number.isFinite(value) && value >= min && value <= max
    ? value
    : undefined;
}

function asRecord(value: unknown): Record<string, unknown> | undefined {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : undefined;
}

function optionalString(record: Record<string, unknown>, key: string): string | undefined {
  return typeof record[key] === 'string' ? record[key] : undefined;
}

function readStoredBrowseStore(value: unknown): Omit<BrowseStore, 'selectionSource'> | null {
  const record = asRecord(value);
  const storeCode = record ? optionalString(record, 'storeCode') : undefined;
  const name = record ? optionalString(record, 'name') : undefined;
  if (!record || !storeCode?.trim() || !name?.trim()) return null;

  return {
    storeCode,
    name,
    address: optionalString(record, 'address'),
    addressLine: optionalString(record, 'addressLine'),
    provinceName: optionalString(record, 'provinceName'),
    communeName: optionalString(record, 'communeName'),
    communeType: optionalString(record, 'communeType'),
    postalCode: optionalString(record, 'postalCode'),
    latitude: validCoordinate(record.latitude, -90, 90),
    longitude: validCoordinate(record.longitude, -180, 180),
    acceptingOrders:
      typeof record.acceptingOrders === 'boolean' ? record.acceptingOrders : undefined,
  };
}

function normalize(
  store: Omit<BrowseStore, 'selectionSource'> | BrowseStore,
  source: BrowseStoreSelectionSource
): BrowseStore {
  const latitude = validCoordinate(store.latitude, -90, 90);
  const longitude = validCoordinate(store.longitude, -180, 180);
  const addressLine = store.addressLine?.trim() || undefined;
  const communeName = store.communeName?.trim() || undefined;
  const provinceName = store.provinceName?.trim() || undefined;
  const postalCode = store.postalCode?.trim() || undefined;
  const composedAddress = [addressLine, communeName, provinceName, postalCode]
    .filter(Boolean)
    .join(', ');
  const savedDisplayAddress = store.address?.trim() || undefined;
  return {
    storeCode: store.storeCode.trim().toUpperCase(),
    name: store.name.trim(),
    address:
      communeName && provinceName
        ? composedAddress || savedDisplayAddress
        : savedDisplayAddress || composedAddress || undefined,
    addressLine,
    provinceName,
    communeName,
    communeType: store.communeType?.trim() || undefined,
    postalCode,
    latitude: latitude !== undefined && longitude !== undefined ? latitude : undefined,
    longitude: latitude !== undefined && longitude !== undefined ? longitude : undefined,
    acceptingOrders: Boolean(store.acceptingOrders),
    selectionSource: source,
  };
}

export const useBrowseStore = create<BrowseStoreState>((set) => ({
  store: initial,
  selectStore: (store, source = 'manual') => {
    const safe = normalize(store, source);
    try {
      if (source === 'manual')
        window.localStorage.setItem(
          STORAGE_KEY,
          JSON.stringify({ version: SCHEMA_VERSION, store: safe })
        );
      // Automatic/QR choices are page-memory only. Retain any earlier manual preference
      // so ending a QR flow does not erase a customer choice.
    } catch {
      /* preference remains in memory */
    }
    set({ store: safe });
  },
  clearStore: () => {
    try {
      window.localStorage.removeItem(STORAGE_KEY);
    } catch {
      /* already cleared in memory */
    }
    set({ store: null });
  },
}));

export const browseStoreStorageKey = STORAGE_KEY;
